import { useState } from "react";
import { CalendarRange, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { fmtLabel } from "@/lib/adminUtils";

export default function DateRangeBar({ range, onApply, onClear, loading }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(range);
  const active = !!(range?.from || range?.to);

  const label = range?.from && range?.to
    ? `${fmtLabel(range.from)} → ${fmtLabel(range.to)}`
    : range?.from
      ? `From ${fmtLabel(range.from)}`
      : range?.to
        ? `Until ${fmtLabel(range.to)}`
        : "All time";

  return (
    <div data-testid="admin-date-range" className="mb-6 flex flex-wrap items-center gap-3 p-4 border border-slate-200 bg-white">
      <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2 mr-2">
        <CalendarRange className="h-3 w-3" /> Date range
      </div>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setDraft(range); }}>
        <PopoverTrigger asChild>
          <button
            data-testid="admin-date-trigger"
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2 bg-white"
          >
            <CalendarRange className="h-4 w-4 text-topo" />
            <span className="mono text-xs tracking-wide">{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0 bg-white" data-testid="admin-date-popover">
          <Calendar
            mode="range"
            selected={draft}
            onSelect={(r) => setDraft(r || { from: undefined, to: undefined })}
            numberOfMonths={2}
            initialFocus
          />
          <div className="flex items-center justify-between gap-2 p-3 border-t border-slate-200 bg-paper-dark/40">
            <button
              onClick={() => setDraft({ from: undefined, to: undefined })}
              data-testid="admin-date-reset-draft"
              className="mono text-[10px] tracking-[0.18em] uppercase text-slate-500 hover:text-slate-900"
            >
              Reset
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setOpen(false)}
                className="rounded-sm border border-slate-300 text-slate-700 text-xs font-semibold px-3 py-1.5 bg-white"
              >
                Cancel
              </button>
              <button
                disabled={loading}
                onClick={() => { onApply(draft || { from: undefined, to: undefined }); setOpen(false); }}
                data-testid="admin-date-apply"
                className="rounded-sm bg-orange-survey hover:bg-orange-survey-dark disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5"
              >
                Apply filter
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {active && (
        <button onClick={onClear} data-testid="admin-date-clear"
          className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-3 py-2 bg-white">
          <X className="h-3 w-3" /> Clear
        </button>
      )}
      {active && (
        <span className="ml-auto mono text-[10px] tracking-[0.18em] uppercase text-emerald-700" data-testid="admin-date-active-badge">
          Filter active
        </span>
      )}
    </div>
  );
}
